/* @flow */
import { DateTime } from 'luxon';

import * as types from './marketActionTypes';
import { getEventId } from 'src/shared/domain/event/selector/eventSelectors';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';
import { patchMembership } from 'src/shared/domain/membership/action/membershipActions';
import { triggerErrorModal } from 'src/shared/app/base/modal/action/modalActions';
import { getMarketSortOrdering } from 'src/shared/app/market/selector/marketSortSelectors';
import {
  featuredFiltersEndpoint,
  topicAvailableFiltersEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';
import {
  extractURLSearchParamsToFilters,
  syncFiltersToURL,
} from 'src/shared/app/market/util/marketUtils';
import {
  canJoinFilterParam,
  conversationTypeFilterParam,
  dateIntervalFilterParam,
  keywordParam,
  kindFilterParam,
  languageFilterParam,
  locationFilterParam,
  orderingParam,
} from 'src/shared/app/market/settings/marketSettings';
import {
  getMarketKindFilter,
  getMarketLanguageFilter,
  getMarketLocationFilter,
  getMarketCanJoinFilter,
  getMarketConversationTypeFilter,
  getMarketDateFilter,
} from 'src/shared/app/market/selector/marketFilterSelectors';
import type { Methods } from 'src/shared/app/base/middleware/httpMethodMiddleware';
import type { Thunk, State, GetState } from 'src/shared/core/type/reduxTypes';
import type { NavigateFunction, Location } from 'react-router';

export function setMarketAvailableFilters(availableFilters: Object) {
  return {
    type: types.SET_MARKET_AVAILABLE_FILTERS,
    availableFilters,
  };
}

export function setMarketStatusFilter(value: string) {
  return {
    type: types.SET_MARKET_STATUS_FILTER,
    value,
  };
}

export function resetMarketStatusFilter() {
  return {
    type: types.RESET_MARKET_STATUS_FILTER,
  };
}

export function setMarketKindFilter(value: Array<string>) {
  return {
    type: types.SET_MARKET_KIND_FILTER,
    value,
  };
}

export function resetMarketKindFilter() {
  return {
    type: types.RESET_MARKET_KIND_FILTER,
  };
}

export function setMarketLanguageFilter(value: Array<string>) {
  return {
    type: types.SET_MARKET_LANGUAGE_FILTER,
    value,
  };
}

export function resetMarketLanguageFilter() {
  return {
    type: types.RESET_MARKET_LANGUAGE_FILTER,
  };
}

export function setMarketDateFilter(value: DateTime) {
  return {
    type: types.SET_MARKET_DATE_FILTER,
    value,
  };
}

export function setMarketLocationFilter(value: Array<number>) {
  return {
    type: types.SET_MARKET_LOCATION_FILTER,
    value,
  };
}

export function setMarketCanJoinFilter(value: boolean) {
  return {
    type: types.SET_MARKET_CANJOIN_FILTER,
    value,
  };
}

export function setMarketConversationTypeFilter(value: Array<string>) {
  return {
    type: types.SET_MARKET_CONVERSATION_TYPE,
    value,
  };
}

export function resetMarketFilters() {
  return {
    type: types.RESET_MARKET_FILTERS,
  };
}

export function setMarketFiltersFromURLSearch(location: Object) {
  return (dispatch: any) => {
    const returnValue: Promise<?Object> = new Promise((resolve) => {
      const values = extractURLSearchParamsToFilters(location);
      if (!values) {
        resolve();
        return;
      }

      if (values[kindFilterParam]) {
        dispatch(setMarketKindFilter(values[kindFilterParam]));
      }

      if (values[dateIntervalFilterParam]) {
        dispatch(
          setMarketDateFilter(
            DateTime.fromISO(values[dateIntervalFilterParam]),
          ),
        );
      }

      if (values[languageFilterParam]) {
        dispatch(setMarketDateFilter(values[languageFilterParam]));
      }

      if (values[locationFilterParam]) {
        dispatch(setMarketLocationFilter(values[locationFilterParam]));
      }

      if (values[canJoinFilterParam]) {
        dispatch(setMarketCanJoinFilter(values[canJoinFilterParam]));
      }

      if (values[orderingParam]) {
        dispatch(setMarketSortOrdering(values[orderingParam]));
      }

      if (values[conversationTypeFilterParam]) {
        dispatch(
          setMarketConversationTypeFilter(values[conversationTypeFilterParam]),
        );
      }

      if (values[keywordParam]) {
        dispatch(setMarketQuery(values[keywordParam]));
      }

      resolve(values);
    });
    return returnValue;
  };
}

export function setMarketSortOrdering(value: string) {
  return {
    type: types.SET_MARKET_SORT_ORDERING,
    value,
  };
}

export function resetMarketSortOrdering() {
  return {
    type: types.RESET_MARKET_SORT_ORDERING,
  };
}

const syncMarketUrlQuery = (
  value?: string,
  location?: Location,
  navigate?: NavigateFunction,
  state: State,
) => {
  if (location && navigate) {
    const kindValue = getMarketKindFilter(state);
    const languageValue = getMarketLanguageFilter(state);
    const dateValue = getMarketDateFilter(state);
    const locationValue = getMarketLocationFilter(state);
    const canJoinValue = getMarketCanJoinFilter(state);
    const sortOrdering = getMarketSortOrdering(state);
    const conversationTypeValue = getMarketConversationTypeFilter(state);

    syncFiltersToURL(
      location,
      navigate,
      kindValue,
      languageValue,
      sortOrdering,
      dateValue,
      canJoinValue,
      locationValue,
      conversationTypeValue,
      value,
    );
  }
};

export function setMarketQuery(
  value: string,
  location?: Location,
  navigate?: NavigateFunction,
) {
  return (dispatch: Function, getState: GetState) => {
    syncMarketUrlQuery(value, location, navigate, getState());

    return dispatch({
      type: types.SET_MARKET_QUERY,
      value,
    });
  };
}

export function setMarketQueryContext(context: string) {
  return {
    type: types.SET_MARKET_QUERY_CONTEXT,
    context,
  };
}

export function resetMarketQuery(
  location?: Location,
  navigate?: NavigateFunction,
) {
  return (dispatch: Function, getState: GetState) => {
    syncMarketUrlQuery(undefined, location, navigate, getState());

    return dispatch({
      type: types.RESET_MARKET_QUERY,
    });
  };
}

export function invalidateMarket() {
  return {
    type: types.INVALIDATE_MARKET,
  };
}

export function completeFavoriteTutorial(eventId: number) {
  return patchMembership(eventId, {
    flags: {
      favorite_tutorial_completed: true,
    },
  });
}

export function fetchTopicsAvailableFilters(): Thunk<Object> {
  return (dispatch: Function, getState: GetState, { get }: Methods) => {
    const state = getState();
    const eventId = getEventId(state);

    return get(topicAvailableFiltersEndpoint(eventId))
      .then(parseResponse)
      .then((json) => dispatch(setMarketAvailableFilters(json)))
      .catch((err) => dispatch(triggerErrorModal(err)));
  };
}

// Featured

export function fetchFeaturedFilters(eventId: number, language: string) {
  return (dispatch: any, _: GetState, { smartGet }: Object) => {
    dispatch(fetchFeaturedFiltersRequest());

    return smartGet(featuredFiltersEndpoint(eventId), { language })
      .then((json) => dispatch(fetchFeaturedFiltersSuccess(json)))
      .catch((exception) => {
        dispatch(fetchFeaturedFiltersFailure(exception));

        throw exception;
      });
  };
}

export function fetchFeaturedFiltersRequest() {
  return {
    type: types.FETCH_FEATURED_FILTERS_REQUEST,
  };
}

export function fetchFeaturedFiltersSuccess(result: Array<string>) {
  return {
    type: types.FETCH_FEATURED_FILTERS_SUCCESS,
    result,
  };
}

export function fetchFeaturedFiltersFailure(exception: Error) {
  return {
    type: types.FETCH_FEATURED_FILTERS_FAILURE,
    exception,
  };
}
